import { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Mail, Check, ArrowRight, MapPin } from "lucide-react";
import { toast } from "sonner";
import { trackEmailSignup } from "../lib/tracking";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";

const MARKETS = [
  "Tampa, FL", "Jacksonville, FL", "Atlanta, GA", "Houston, TX",
  "Dallas, TX", "Orlando, FL", "San Antonio, TX", "Miami, FL",
  "Charlotte, NC", "Nashville, TN", "Phoenix, AZ", "Detroit, MI",
];

export default function EmailCapture({ variant = "inline", heading, subtext }) {
  const [email, setEmail] = useState("");
  const [market, setMarket] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email, market: market || undefined });
      setDone(true);
      trackEmailSignup(market || "all");
      toast.success("You're in! Watch your inbox for top deals.");
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className={`flex items-center gap-2 ${variant === "banner" ? "justify-center py-6" : "py-3"}`} data-testid="email-capture-success">
        <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
          <Check className="w-4 h-4 text-emerald-500" />
          <span className="text-sm font-medium text-emerald-500">You're subscribed! Top deals coming to your inbox weekly.</span>
        </div>
      </div>
    );
  }

  if (variant === "panel") {
    return (
      <form onSubmit={handleSubmit} noValidate className="flex flex-col sm:flex-row gap-3 w-full" data-testid="email-capture-panel">
        <label htmlFor="email-capture-market" className="sr-only">Market</label>
        <div className="relative sm:w-44 lg:w-48">
          <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-rd-muted pointer-events-none" aria-hidden="true" />
          <select
            id="email-capture-market"
            value={market}
            onChange={(e) => setMarket(e.target.value)}
            className="rd-input appearance-none cursor-pointer pl-10 pr-8 text-rd-text"
            data-testid="email-capture-market"
          >
            <option value="">All markets</option>
            {MARKETS.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <label htmlFor="email-capture-input" className="sr-only">Email address</label>
        <div className="relative flex-1">
          <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-rd-muted pointer-events-none" aria-hidden="true" />
          <input
            id="email-capture-input"
            type="email"
            placeholder="your@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="rd-input pl-10"
            data-testid="email-capture-input"
          />
        </div>
        <button type="submit" className="rd-btn rd-btn-primary h-[52px] px-6" disabled={loading} data-testid="email-capture-submit">
          {loading ? "Subscribing…" : <>Get Deal Alerts <ArrowRight className="w-4 h-4" aria-hidden="true" /></>}
        </button>
      </form>
    );
  }

  if (variant === "banner") {
    return (
      <section className="bg-emerald-600/[0.04] border-y border-emerald-500/10 py-10 px-4 sm:px-6" data-testid="email-capture-banner">
        <div className="max-w-[600px] mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Mail className="w-4 h-4 text-emerald-500" />
            <span className="text-xs font-medium text-emerald-500 uppercase tracking-wider">Free Deal Alerts</span>
          </div>
          <h3 className="text-lg sm:text-xl font-bold mb-1">{heading || "Get top real estate deals sent to your inbox"}</h3>
          <p className="text-sm text-muted-foreground mb-5">{subtext || "Weekly digest of the highest cash-flow properties, market insights, and investment opportunities."}</p>
          <form onSubmit={handleSubmit} className="space-y-3 max-w-sm mx-auto">
            <Input
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-11 text-sm bg-card border-border"
              data-testid="email-capture-input"
            />
            <div className="flex gap-2">
              <div className="relative flex-1">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                <select
                  value={market}
                  onChange={(e) => setMarket(e.target.value)}
                  className="w-full h-10 pl-8 pr-3 text-sm bg-card border border-border rounded-md text-muted-foreground appearance-none cursor-pointer focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                  data-testid="email-capture-market"
                >
                  <option value="">All markets</option>
                  {MARKETS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <Button type="submit" className="h-10 bg-emerald-600 hover:bg-emerald-700 text-white px-5 flex-shrink-0" disabled={loading} data-testid="email-capture-submit">
                {loading ? "..." : <>Get Deal Alerts <ArrowRight className="w-3.5 h-3.5 ml-1.5" /></>}
              </Button>
            </div>
          </form>
          <p className="text-[10px] text-muted-foreground/60 mt-3">Join 12,800+ investors. Unsubscribe anytime. No spam.</p>
        </div>
      </section>
    );
  }

  // Inline variant (compact)
  return (
    <form onSubmit={handleSubmit} className="flex gap-2" data-testid="email-capture-inline">
      <Input
        type="email"
        placeholder="your@email.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="h-9 text-sm bg-card border-border max-w-[200px]"
        data-testid="email-capture-input"
      />
      <Button type="submit" size="sm" className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white" disabled={loading} data-testid="email-capture-submit">
        {loading ? "..." : "Get Alerts"}
      </Button>
    </form>
  );
}
